import { useEffect, useRef, useState } from 'react';
import { contactVerificationCode } from '../contacts/contact';
import { formatBytes } from '../lib/format';
import type { StoredContact, StoredPreparedParcel, StoredTransferHistory } from '../lib/storage';

export function ContactSections({ contacts, parcels, history, onMeet, onPrepare, onReadyReceive, onRename, onForget, onDeleteParcel, onClearHistory }: {
  contacts: StoredContact[];
  parcels: StoredPreparedParcel[];
  history: StoredTransferHistory[];
  onMeet: (contact: StoredContact) => void;
  onPrepare: (contact: StoredContact) => void;
  onReadyReceive: (contact: StoredContact) => void;
  onRename: (contact: StoredContact, name: string) => void;
  onForget: (contact: StoredContact) => void;
  onDeleteParcel: (parcel: StoredPreparedParcel) => void;
  onClearHistory: () => void;
}) {
  if (!contacts.length && !history.length) return null;
  return <div className="local-data-grid">
    {contacts.length > 0 && <section className="card contacts-card" aria-labelledby="contacts-title">
      <span className="eyebrow">LOCAL ONLY</span>
      <h2 id="contacts-title">Trusted contacts</h2>
      <p className="section-intro">Both people click the saved contact when they have agreed to meet. PairFerry does not maintain an online contact list.</p>
      <div className="contact-list">
        {contacts.map((contact) => <ContactCard
          key={contact.id}
          contact={contact}
          parcels={parcels.filter((parcel) => parcel.contactId === contact.id)}
          onMeet={() => onMeet(contact)}
          onPrepare={() => onPrepare(contact)}
          onReadyReceive={() => onReadyReceive(contact)}
          onRename={(name) => onRename(contact, name)}
          onForget={() => onForget(contact)}
          onDeleteParcel={onDeleteParcel}
        />)}
      </div>
    </section>}
    {history.length > 0 && <section className="card history-card" aria-labelledby="history-title">
      <span className="eyebrow">THIS BROWSER</span>
      <div className="section-heading"><h2 id="history-title">Completed transfers</h2><button className="button ghost compact" onClick={onClearHistory}>Clear history</button></div>
      <p className="section-intro">Privacy-safe summaries only—no filenames, invitation secrets, or addresses.</p>
      <div className="history-list">
        {history.slice(0, 12).map((item) => <div className="history-row" key={item.id}>
          <span aria-hidden="true">✓</span>
          <span><strong>{item.direction === 'sending' ? 'Sent to' : 'Received from'} {item.peerLabel}</strong><small>{item.fileCount} file{item.fileCount === 1 ? '' : 's'} · {formatBytes(item.totalBytes)} · {new Date(item.completedAt).toLocaleString()}</small></span>
        </div>)}
      </div>
    </section>}
  </div>;
}

function ContactCard({ contact, parcels, onMeet, onPrepare, onReadyReceive, onRename, onForget, onDeleteParcel }: {
  contact: StoredContact;
  parcels: StoredPreparedParcel[];
  onMeet: () => void;
  onPrepare: () => void;
  onReadyReceive: () => void;
  onRename: (name: string) => void;
  onForget: () => void;
  onDeleteParcel: (parcel: StoredPreparedParcel) => void;
}) {
  const [verification, setVerification] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(contact.name);
  const renameInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { void contactVerificationCode(contact.secret).then(setVerification); }, [contact.secret]);
  useEffect(() => {
    if (renaming) renameInputRef.current?.focus();
  }, [renaming]);
  useEffect(() => setNameDraft(contact.name), [contact.name]);
  const ready = Boolean(contact.oneTimeReceive && contact.oneTimeReceive.expiresAt > Date.now());
  return <article className="contact-card">
    <div className="contact-heading">
      <span className="contact-avatar" aria-hidden="true">{contact.name.slice(0, 1).toUpperCase()}</span>
      <span><strong>{contact.name}</strong><small>{contact.lastMetAt ? `Last met ${new Date(contact.lastMetAt).toLocaleString()}` : 'Saved locally · not met again yet'}</small></span>
      {ready && <span className="ready-chip">Ready once</span>}
    </div>
    {renaming && <form className="rename-contact-form" onSubmit={(event) => {
      event.preventDefault();
      const nextName = nameDraft.trim();
      if (!nextName) return;
      onRename(nextName);
      setRenaming(false);
    }}>
      <label className="field">Contact name
        <input ref={renameInputRef} value={nameDraft} maxLength={40} autoComplete="off" enterKeyHint="done" onChange={(event) => setNameDraft(event.target.value)} />
      </label>
      <div className="button-row">
        <button className="button primary compact" type="submit" disabled={!nameDraft.trim()}>Save name</button>
        <button className="button ghost compact" type="button" onClick={() => { setNameDraft(contact.name); setRenaming(false); }}>Cancel</button>
      </div>
    </form>}
    <p className="verification-code"><span>Verification phrase</span><strong>{verification || '…'}</strong></p>
    {parcels.map((parcel) => <div className="prepared-row" key={parcel.id}>
      <span><strong>Prepared parcel</strong><small>{parcel.files.length} file{parcel.files.length === 1 ? '' : 's'} · {formatBytes(parcel.totalBytes)}</small></span>
      <button className="button ghost compact" onClick={() => onDeleteParcel(parcel)}>Remove</button>
    </div>)}
    <div className="button-row contact-actions">
      <button className="button primary" onClick={onMeet}>Meet</button>
      <button className="button secondary" onClick={onPrepare}>Prepare parcel</button>
      <button className="button secondary" onClick={onReadyReceive}>Receive once</button>
      <button className="button ghost" onClick={() => { setNameDraft(contact.name); setRenaming(true); }}>Rename</button>
      <button className="button ghost danger" onClick={onForget}>Forget</button>
    </div>
  </article>;
}
